import { FC, lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useIntercom } from 'hooks/useIntercom'
import { useHideExtLoadopsSticky } from 'hooks/useHideExtLoadopsSticky'
import { useFeatureTracking } from 'hooks/useFeatureTracking'
import { LazyFallback } from 'components/LazyFallback'

import MasterLayout from '../layouts/MasterLayout'
import AuthLayout from '../layouts/AuthLayout'

// Lazy-loaded route components
const DashboardRoutes = lazy(() => import('./dashboard/routes'))
const AuthRoutes = lazy(() => import('./auth/routes'))

// Public pages
const ExtensionSuccessPage = lazy(() => import('./dashboard/pages/extension/ExtensionSuccessPage'))
const UninstallSuccessPage = lazy(() => import('./dashboard/pages/extension/UninstallSuccessPage'))
const PaymentSuccess = lazy(() => import('./payment/PaymentSuccess').then((m) => ({ default: m.PaymentSuccess })))
const PaymentCancelled = lazy(() => import('./payment/PaymentCancelled').then((m) => ({ default: m.PaymentCancelled })))
const SubscribePage = lazy(() => import('./subscribe/SubscribePage').then((m) => ({ default: m.SubscribePage })))
const GmailCallback = lazy(() => import('./auth/pages/callbacks/GmailCallback'))
const MicrosoftCallback = lazy(() => import('./auth/pages/callbacks/MicrosoftCallback'))
const NylasCallback = lazy(() => import('./auth/pages/callbacks/NylasCallback'))
const SamsaraCallback = lazy(() => import('./auth/pages/callbacks/SamsaraCallback'))
const MotiveCallback = lazy(() => import('./auth/pages/callbacks/MotiveCallback'))
const InfluencerLogin = lazy(() => import('./influencer/InfluencerLogin').then((m) => ({ default: m.InfluencerLogin })))
const InfluencerDashboard = lazy(() => import('./influencer/InfluencerDashboard').then((m) => ({ default: m.InfluencerDashboard })))
const InfluencerCodes = lazy(() => import('./influencer/InfluencerCodes').then((m) => ({ default: m.InfluencerCodes })))
const InfluencerReferrals = lazy(() => import('./influencer/InfluencerReferrals').then((m) => ({ default: m.InfluencerReferrals })))
const AdminBrokerAccounts = lazy(() => import('./broker-portal/AdminBrokerAccounts').then((m) => ({ default: m.AdminBrokerAccounts })))
const BrokerPortalRoutes = lazy(() => import('./broker-portal/routes'))
const OnboardingPage = lazy(() => import('./auth/pages/onboarding/Onboarding'))
const LoginPage = lazy(() => import('./auth/pages/login/Login'))
const ExtensionSignup = lazy(() => import('./dashboard/pages/extension-signup/SignUp'))
const UpdaterLiteSignup = lazy(() =>
    import('./dashboard/pages/updater-agent/pages/updater-agent-lite/components').then((m) => ({ default: m.UpdaterLiteSignup }))
)
const PublicTrackingPage = lazy(() => import('./public-tracking').then((m) => ({ default: m.PublicTrackingPage })))
const PublicAgreementPage = lazy(() => import('./public-agreement/PublicAgreementPage').then((m) => ({ default: m.PublicAgreementPage })))
const PublicPricingPage = lazy(() => import('../features/billing/pages/PublicPricingPage').then((m) => ({ default: m.PublicPricingPage })))
const MobilePricingPage = lazy(() => import('../features/billing/pages/MobilePricingPage').then((m) => ({ default: m.MobilePricingPage })))
const MobileBillingSuccessPage = lazy(() => import('../features/billing/pages/MobileBillingSuccessPage').then((m) => ({ default: m.MobileBillingSuccessPage })))
const MobileBillingCancelPage = lazy(() => import('../features/billing/pages/MobileBillingCancelPage').then((m) => ({ default: m.MobileBillingCancelPage })))
const MobileBillingReturnPage = lazy(() => import('../features/billing/pages/MobileBillingReturnPage').then((m) => ({ default: m.MobileBillingReturnPage })))
const TwaRadarPage = lazy(() => import('./twa/TwaRadarPage'))
const SharedLoadPage = lazy(() => import('./shared-load/SharedLoadPage').then((m) => ({ default: m.SharedLoadPage })))
const ReferralLandingPage = lazy(() => import('./referral/ReferralLandingPage').then((m) => ({ default: m.ReferralLandingPage })))
const MobileAppLinkPage = lazy(() => import('./mobile-app-link/MobileAppLinkPage').then((m) => ({ default: m.MobileAppLinkPage })))
const InviteLandingPage = lazy(() => import('./mobile-app-link/InviteLandingPage').then((m) => ({ default: m.InviteLandingPage })))
const DriverAppLinkLandingPage = lazy(() => import('./mobile-app-link/DriverAppLinkLandingPage').then((m) => ({ default: m.DriverAppLinkLandingPage })))
const PromoAccountingPage = lazy(() => import('../features/promo-accounting').then((m) => ({ default: m.PromoAccountingPage })))
const PromoDispatchPage = lazy(() => import('../features/promo-dispatch').then((m) => ({ default: m.PromoDispatchPage })))
const AiAssistantConnectPage = lazy(() => import('../features/ai-assistant-connect').then((m) => ({ default: m.AiAssistantConnectPage })))
const ResumeAppHandOff = lazy(() => import('../features/ai-assistant-connect').then((m) => ({ default: m.ResumeAppHandOff })))
const MessagingConnectPage = lazy(() => import('../features/messaging-connect').then((m) => ({ default: m.MessagingConnectPage })))
const EmailConnectPage = lazy(() => import('../features/messaging-connect').then((m) => ({ default: m.EmailConnectPage })))
const EmailConnectReturn = lazy(() => import('../features/messaging-connect').then((m) => ({ default: m.EmailConnectReturn })))

// Mobile onboarding pages (served at /mobile-onboarding for mobile users)
const MobileOnboardingLanding = lazy(() => import('./mobile-onboarding/pages/MobileOnboardingLanding').then((m) => ({ default: m.MobileOnboardingLanding })))
const MobileOnboardingPassword = lazy(() => import('./mobile-onboarding/pages/MobileOnboardingPassword').then((m) => ({ default: m.MobileOnboardingPassword })))
const MobileOnboardingInstall = lazy(() => import('./mobile-onboarding/pages/MobileOnboardingInstall').then((m) => ({ default: m.MobileOnboardingInstall })))
const MobileOnboardingRedirect = lazy(() => import('./mobile-onboarding/pages/MobileOnboardingRedirect').then((m) => ({ default: m.MobileOnboardingRedirect })))
const MobileOnboardingDesktopGuard = lazy(() =>
    import('./mobile-onboarding/components/MobileOnboardingDesktopGuard').then((m) => ({ default: m.MobileOnboardingDesktopGuard }))
)

// Onboarding pages (default, served at /onboarding for desktop users)
const OnboardingLanding = lazy(() => import('./onboarding/pages/OnboardingLanding').then((m) => ({ default: m.OnboardingLanding })))
const OnboardingOTPVerify = lazy(() => import('./onboarding/pages/OnboardingOTPVerify').then((m) => ({ default: m.OnboardingOTPVerify })))
const OnboardingCreatePassword = lazy(() => import('./onboarding/pages/OnboardingCreatePassword').then((m) => ({ default: m.OnboardingCreatePassword })))
const OnboardingLogin = lazy(() => import('./onboarding/pages/OnboardingLogin').then((m) => ({ default: m.OnboardingLogin })))
const OnboardingForgotPassword = lazy(() => import('./onboarding/pages/OnboardingForgotPassword').then((m) => ({ default: m.OnboardingForgotPassword })))
const OnboardingInstallExtension = lazy(() => import('./onboarding/pages/OnboardingInstallExtension').then((m) => ({ default: m.OnboardingInstallExtension })))
const OnboardingConnectEmail = lazy(() => import('./onboarding/pages/OnboardingConnectEmail').then((m) => ({ default: m.OnboardingConnectEmail })))
const OnboardingEmailTemplate = lazy(() => import('./onboarding/pages/OnboardingEmailTemplate').then((m) => ({ default: m.OnboardingEmailTemplate })))
const OnboardingAuthGuard = lazy(() => import('./onboarding/components/OnboardingAuthGuard').then((m) => ({ default: m.OnboardingAuthGuard })))
const OnboardingMobileGuard = lazy(() => import('./onboarding/components/OnboardingMobileGuard').then((m) => ({ default: m.OnboardingMobileGuard })))

// TMS Onboarding pages
const AuthTms = lazy(() => import('./tms-onboarding/pages/AuthTms').then((m) => ({ default: m.AuthTms })))
const AuthTmsSetPassword = lazy(() => import('./tms-onboarding/pages/AuthTmsSetPassword').then((m) => ({ default: m.AuthTmsSetPassword })))
const AuthTmsForgotPassword = lazy(() => import('./tms-onboarding/pages/AuthTmsForgotPassword').then((m) => ({ default: m.AuthTmsForgotPassword })))
const OnboardingTmsCompanyDetails = lazy(() =>
    import('./tms-onboarding/pages/OnboardingTmsCompanyDetails').then((m) => ({ default: m.OnboardingTmsCompanyDetails }))
)
const AgenticOnboardingPage = lazy(() => import('./tms-onboarding/pages/AgenticOnboardingPage').then((m) => ({ default: m.AgenticOnboardingPage })))

// LoadHub pages
const AuthHub = lazy(() => import('./load-hub/pages/AuthHub').then((m) => ({ default: m.AuthHub })))
const AuthHubSetPassword = lazy(() => import('./load-hub/pages/AuthHubSetPassword').then((m) => ({ default: m.AuthHubSetPassword })))
const AuthHubForgotPassword = lazy(() => import('./load-hub/pages/AuthHubForgotPassword').then((m) => ({ default: m.AuthHubForgotPassword })))
const OnboardingHub = lazy(() => import('./load-hub/pages/OnboardingHub').then((m) => ({ default: m.OnboardingHub })))
const OnboardingHubInstallExtension = lazy(() =>
    import('./load-hub/pages/OnboardingHubInstallExtension').then((m) => ({ default: m.OnboardingHubInstallExtension }))
)

/** Redirects `/ext-v2/foo` → `/mobile-onboarding/foo`. */
const MobileOnboardingSubRouteRedirect = () => {
    const location = useLocation()
    const newPath = location.pathname.replace('/ext-v2', '/mobile-onboarding')
    return <Navigate to={`${newPath}${location.search}`} replace />
}

/**
 * Backward-compat: forward each retired `/ext/*` path to its new `/mobile-onboarding/*` home.
 * The form-step routes (`/ext/signup`, `/ext/verify`, `/ext/success`) are collapsed into the
 * single-page state machine at `/mobile-onboarding`, so they all land on the root.
 */
const ExtLegacyRedirect: FC<{ to: string }> = ({ to }) => {
    const location = useLocation()
    return <Navigate to={`${to}${location.search}${location.hash}`} replace />
}

export default function AppRoutes() {
    // Initialize Intercom on ext and dispatcher routes
    useIntercom({
        enabledRoutes: ['/mobile-onboarding', '/ext', '/ext-v2', '/onboarding', '/dispatcher', '/spot', '/auth', '/auth/hub', '/help-center', '/agent'],
    })

    // Hide the Numeo extension's injected LoadOps sticky button on box-truck routes.
    useHideExtLoadopsSticky()

    // Tag GlitchTip events with the current feature (derived from the route)
    useFeatureTracking()

    return (
        <Suspense fallback={<LazyFallback />}>
            <Routes>
                {/* Public callbacks */}
                {/* A return from the texted email-connect page goes back to it before the callback mounts (ADR-054). */}
                <Route
                    path="auth/gmail/callback"
                    element={
                        <EmailConnectReturn provider="google">
                            <GmailCallback />
                        </EmailConnectReturn>
                    }
                />
                {/* Temporary: keep old signup callback route for in-flight OAuth flows */}
                <Route path="auth/google-gmail/callback" element={<GmailCallback />} />
                <Route
                    path="auth/microsoft/callback"
                    element={
                        <EmailConnectReturn provider="microsoft">
                            <MicrosoftCallback />
                        </EmailConnectReturn>
                    }
                />
                <Route path="auth/nylas/callback" element={<NylasCallback />} />
                <Route path="auth/samsara/callback" element={<SamsaraCallback />} />
                <Route path="auth/motive/callback" element={<MotiveCallback />} />
                <Route path="auth/onboarding" element={<OnboardingPage />} />
                <Route
                    path="auth/sign-in"
                    element={
                        <>
                            {/* Resumes an AI assistant sign-in handed to the Numeo app if the browser reloaded this page (ADR-045.1 §3). */}
                            <ResumeAppHandOff />
                            <LoginPage />
                        </>
                    }
                />
                <Route path="tracking/:token" element={<PublicTrackingPage />} />
                <Route path="agreements/sign/:token" element={<PublicAgreementPage />} />

                {/* Mobile onboarding pages — MUST be before auth/* to avoid being caught by AuthLayout.
                    Wrapped in MobileOnboardingDesktopGuard: if viewport is too wide for a phone,
                    bounce to the desktop /onboarding flow. */}
                <Route element={<MobileOnboardingDesktopGuard />}>
                    <Route path="/mobile-onboarding" element={<MobileOnboardingLanding />} />
                    <Route path="/mobile-onboarding/set-password" element={<MobileOnboardingPassword />} />
                    <Route path="/mobile-onboarding/install" element={<MobileOnboardingInstall />} />
                </Route>

                {/* Backward-compat: legacy `/ext/*` URLs → `/mobile-onboarding/*`. Kept for in-flight
                    sessions, marketing links, and email CTAs that still point at the old paths. */}
                <Route path="/ext" element={<ExtLegacyRedirect to="/mobile-onboarding" />} />
                <Route path="/ext/signup" element={<ExtLegacyRedirect to="/mobile-onboarding" />} />
                <Route path="/ext/verify" element={<ExtLegacyRedirect to="/mobile-onboarding" />} />
                <Route path="/ext/set-password" element={<ExtLegacyRedirect to="/mobile-onboarding/set-password" />} />
                <Route path="/ext/success" element={<ExtLegacyRedirect to="/mobile-onboarding/install" />} />

                {/* Backward-compat: legacy `/ext-v2` → feature-flag-driven redirect (desktop/mobile split). */}
                <Route path="/ext-v2" element={<MobileOnboardingRedirect />} />
                <Route path="/ext-v2/*" element={<MobileOnboardingSubRouteRedirect />} />

                {/* Onboarding pages (default, served at /onboarding) — mobile users are redirected to /mobile-onboarding by OnboardingMobileGuard */}
                <Route element={<OnboardingMobileGuard />}>
                    {/* Public onboarding pages */}
                    <Route path="/onboarding" element={<OnboardingLanding />} />
                    <Route path="/onboarding/verify" element={<OnboardingOTPVerify />} />
                    <Route path="/onboarding/create-password" element={<OnboardingCreatePassword />} />
                    <Route path="/onboarding/login" element={<OnboardingLogin />} />
                    <Route path="/onboarding/forgot-password" element={<OnboardingForgotPassword />} />

                    {/* Protected onboarding pages */}
                    <Route
                        path="/onboarding/install"
                        element={
                            <OnboardingAuthGuard>
                                <OnboardingInstallExtension />
                            </OnboardingAuthGuard>
                        }
                    />
                    <Route
                        path="/onboarding/connect-email"
                        element={
                            <OnboardingAuthGuard>
                                <OnboardingConnectEmail />
                            </OnboardingAuthGuard>
                        }
                    />
                    <Route
                        path="/onboarding/email-template"
                        element={
                            <OnboardingAuthGuard>
                                <OnboardingEmailTemplate />
                            </OnboardingAuthGuard>
                        }
                    />
                </Route>

                {/* LoadHub auth pages — public (pre-auth) */}
                <Route path="/auth/hub" element={<AuthHub />} />
                <Route path="/auth/hub/set-password" element={<AuthHubSetPassword />} />
                <Route path="/auth/hub/forgot-password" element={<AuthHubForgotPassword />} />

                {/* LoadHub onboarding pages — protected (post-auth). Unauth users bounce to /auth/hub. */}
                <Route
                    path="/onboarding/hub/install-extension"
                    element={
                        <OnboardingAuthGuard redirectTo="/auth/hub">
                            <OnboardingHubInstallExtension />
                        </OnboardingAuthGuard>
                    }
                />
                <Route
                    path="/onboarding/hub/company-details"
                    element={
                        <OnboardingAuthGuard redirectTo="/auth/hub">
                            <OnboardingHub />
                        </OnboardingAuthGuard>
                    }
                />

                {/* TMS auth pages — public (pre-auth) */}
                <Route path="/auth/tms" element={<AuthTms />} />
                <Route path="/auth/tms/set-password" element={<AuthTmsSetPassword />} />
                <Route path="/auth/tms/forgot-password" element={<AuthTmsForgotPassword />} />

                {/* TMS onboarding pages — protected (post-auth). Unauth users bounce to /auth/tms. */}
                <Route
                    path="/onboarding/tms/company-details"
                    element={
                        <OnboardingAuthGuard redirectTo="/auth/tms">
                            <OnboardingTmsCompanyDetails />
                        </OnboardingAuthGuard>
                    }
                />
                {/* Agentic post-signup wizard (role → company → ELD → fleet). Fullscreen, no sidebar. */}
                <Route
                    path="/onboarding/tms/agentic"
                    element={
                        <OnboardingAuthGuard redirectTo="/auth/tms">
                            <AgenticOnboardingPage />
                        </OnboardingAuthGuard>
                    }
                />
                {/* /onboarding/tms/welcome is handled by DashboardRoutes inside MasterLayout (sidebar) */}

                {/* Influencer portal */}
                <Route path="/influencer/login" element={<InfluencerLogin />} />
                <Route path="/influencer/dashboard" element={<InfluencerDashboard />} />
                <Route path="/influencer/codes" element={<InfluencerCodes />} />
                <Route path="/influencer/referrals" element={<InfluencerReferrals />} />

                {/* Broker portal */}
                <Route path="/broker-portal/*" element={<BrokerPortalRoutes />} />

                {/* Admin — broker portal account management (dispatcher JWT required) */}
                <Route path="/admin/broker-accounts" element={<AdminBrokerAccounts />} />

                {/* Telegram Mini App — no auth required, uses Telegram initData */}
                <Route path="/twa/load-radar" element={<TwaRadarPage />} />
                {/* Browser fallback for the mobile app's /load/* Universal Links. */}
                <Route path="/load/:externalId" element={<SharedLoadPage />} />
                <Route path="/shared-load/:externalId" element={<SharedLoadPage />} />

                {/* ADR-019 §4 — public referral landing (driver opens app, dispatcher funnels to signup) */}
                <Route path="/r/:type/:code" element={<ReferralLandingPage />} />
                {/* Mobile app invites, when the app did not open the link. */}
                {/* A driver sign-in link, when the app did not open it. Ranked above `:type` by its static segment. */}
                <Route path="/invite/app-link/:token" element={<DriverAppLinkLandingPage />} />
                <Route path="/invite/:type/:token" element={<InviteLandingPage />} />

                {/* Accounting promo lead form — linked from the mobile home-layout promo banner.
                    PUBLIC: a sign-in wall in front of a promo form loses leads. A signed-in
                    visitor still gets the form pre-filled from their profile and the lead
                    attributed to their project. Fullscreen (no sidebar), reached by direct URL. */}
                <Route path="/promo-accounting" element={<PromoAccountingPage />} />

                {/* Dispatch promo lead form — campaign landing page. PUBLIC: a sign-in wall
                    in front of a promo form loses leads. A signed-in visitor still gets the
                    form pre-filled from their profile and the lead attributed to their
                    project. Fullscreen (no sidebar), reached by direct URL. */}
                <Route path="/promo-dispatch" element={<PromoDispatchPage />} />

                {/* The MCP gateway's connect page (ADR-045.1 §3): an assistant's sign-in lands here. It needs the
                    user's Numeo login, so the page itself sends an unauthenticated visitor to sign in with a
                    ?redirect= back here (the dashboard guard would drop the ?request= query). Fullscreen. */}
                <Route path="/connect/ai-assistant" element={<AiAssistantConnectPage />} />

                {/* Texting connect page (ADR-053); signs in with ?redirect= back here, like the MCP page above. */}
                <Route path="/connect/imessage" element={<MessagingConnectPage />} />

                {/* Email connect by text (ADR-054): no Numeo login, the texted link names the account. */}
                <Route path="/connect/email" element={<EmailConnectPage />} />

                {/* Public pricing page */}
                <Route path="/numeai-pricing" element={<PublicPricingPage />} />

                {/* Mobile subscription pages — opened in in-app browser from mobile app */}
                <Route path="/m/pricing" element={<MobilePricingPage />} />
                <Route path="/m/billing/success" element={<MobileBillingSuccessPage />} />
                <Route path="/m/billing/cancel" element={<MobileBillingCancelPage />} />
                <Route path="/m/billing/return" element={<MobileBillingReturnPage />} />
                {/* Any other /m/<path> is a screen in the mobile app (`@numeo/types` MOBILE_DEEPLINK_PATHS). */}
                <Route path="/m/*" element={<MobileAppLinkPage />} />

                {/* Public registration pages */}
                <Route path="/register/from-extension" element={<ExtensionSignup />} />
                <Route path="/subscribe" element={<SubscribePage />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancelled" element={<PaymentCancelled />} />
                <Route path="/extension/success" element={<ExtensionSuccessPage />} />
                <Route path="/extension/uninstall" element={<UninstallSuccessPage />} />
                <Route path="/register/updater-lite" element={<UpdaterLiteSignup />} />

                {/* Auth routes with AuthLayout */}
                <Route
                    path="auth/*"
                    element={
                        <AuthLayout>
                            <AuthRoutes />
                        </AuthLayout>
                    }
                />

                {/* Protected dashboard routes - MUST be last as it's a catch-all */}
                <Route
                    path="*"
                    element={
                        <MasterLayout>
                            <DashboardRoutes />
                        </MasterLayout>
                    }
                />
            </Routes>
        </Suspense>
    )
}
